import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Map, MapboxOptions, NavigationControl } from 'mapbox-gl';
import { IMapIconModel } from '@types-custom/models/ui/map-icons.model';

@Injectable()
export class MapboxInstanceService implements OnDestroy {
  private _containerId: string | undefined;
  private mapSubject = new BehaviorSubject<mapboxgl.Map | undefined>(undefined);
  private mapStatusSubject = new BehaviorSubject('off');

  map$ = this.mapSubject.asObservable();
  mapStatus$ = this.mapStatusSubject.asObservable();

  private subscriptions: Subscription[] = [];

  get mapInstance(): mapboxgl.Map | undefined {
    return this.mapSubject.value;
  }

  get containerId(): string | undefined {
    return this._containerId;
  }

  constructor(@Inject('environment') private environment: any) { }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private buildMap(options?: MapboxOptions): mapboxgl.Map {
    const mapInstance = new Map(options);
    return mapInstance;
  }

  private getDefaultOptions(container: string): MapboxOptions {
    return {
      container,
      ...this.environment.mapbox,
    };
  }

  initializeMap(data: string | MapboxOptions, mapIcons: IMapIconModel[]): void {
    let map: Map;
    if (typeof data === 'string') {
      this._containerId = data;
      const options = this.getDefaultOptions(data);
      map = this.buildMap(options);
    } else {
      this._containerId = data.container as string;
      map = this.buildMap(data);
    }
    map.once('idle', () => {
      this.mapSubject.next(map);
      this.loadIcons(mapIcons);
    });
    map.once('load', () => {
      this.mapStatusSubject.next('loaded');
    });
    map.addControl(new NavigationControl(), 'bottom-left');
  }

  loadIcons(mapIcons: IMapIconModel[]) {
    mapIcons.forEach((icon: any) => {

      const image = new Image();
      image.src = icon.path;
      const unsbicon = fromEvent(image, 'load').subscribe(() =>
        this.mapInstance?.addImage(icon.id, image)
      );
      this.subscriptions.push(unsbicon);
    });
  }

  setFocus(lat: number, lng: number, zoom: number = 16): void {
    this.mapInstance?.flyTo({ center: [lng, lat], zoom });
  }

  setDefaultFocus(): void {
    const { zoom, center: [lng, lat] } = this.environment.mapbox;
    this.setFocus(lat, lng, zoom);
  }
}
